import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getNcodesAPI,
  getResultAPI,
  submitQuizAPI,
  getPersonalityTestOptionsAPI,
  submitPersonalityTestAPI,
  submitInnerNeedsTestAPI,
  submitTraitTestAPI,
  checkTokenExpiryAPI,
  reorderNcodesAPI,
  submitProspectCompanyAPI,
} from './toolAPI';

// Existing thunks
export const fetchNcodes = createAsyncThunk(
  'tool/fetchNcodes',
  async (id, { rejectWithValue }) => {
    try {
      const data = await getNcodesAPI(id);
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch ncodes'
      );
    }
  }
);

// Updated to accept ids, token, and id (query parameter)
export const fetchResult = createAsyncThunk(
  'tool/fetchResult',
  async ({ ids, token, id }, { rejectWithValue }) => {
    try {
      const data = await getResultAPI(ids, token, id);
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch result'
      );
    }
  }
);

// Updated thunk for quiz submit to accept payload and token
export const submitQuiz = createAsyncThunk(
  'tool/submitQuiz',
  async ({ payload, token }, { rejectWithValue }) => {
    try {
      const data = await submitQuizAPI(payload, token);
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to submit quiz'
      );
    }
  }
);

// New thunk for personality test options
export const fetchPersonalityTestOptions = createAsyncThunk(
  'tool/fetchPersonalityTestOptions',
  async (_, { rejectWithValue }) => {
    try {
      const data = await getPersonalityTestOptionsAPI();
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch personality test options'
      );
    }
  }
);

// Updated thunk for personality test submit
export const submitPersonalityTest = createAsyncThunk(
  'tool/submitPersonalityTest',
  async ({ payload, token }, { rejectWithValue }) => {
    try {
      const data = await submitPersonalityTestAPI(payload, token);
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to submit personality test'
      );
    }
  }
);

// Updated thunk for inner needs test submit
export const submitInnerNeedsTest = createAsyncThunk(
  'inner-need/submit',
  async ({ payload, token }, { rejectWithValue }) => {
    try {
      const data = await submitInnerNeedsTestAPI(payload, token);
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to submit inner needs test'
      );
    }
  }
);

// Updated thunk for trait test submit
export const submitTraitTest = createAsyncThunk(
  'assessment/submit',
  async ({ payload, token }, { rejectWithValue }) => {
    try {
      const data = await submitTraitTestAPI(payload, token);
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to submit trait test'
      );
    }
  }
);

// ✅ NEW: Thunk for checking token expiry
export const checkTokenExpiry = createAsyncThunk(
  'tool/checkTokenExpiry',
  async (token, { rejectWithValue }) => {
    try {
      const data = await checkTokenExpiryAPI(token);
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to check token expiry'
      );
    }
  }
);

// Thunk to submit final reordered baselines
export const reorderBaselines = createAsyncThunk(
  'tool/reorderBaselines',
  async ({ payload, token }, { rejectWithValue }) => {
    try {
      const data = await reorderNcodesAPI(payload, token);
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to reorder baselines'
      );
    }
  }
);

// ✅ NEW: Thunk to submit prospect company assessment
export const submitProspectCompany = createAsyncThunk(
  'tool/submitProspectCompany',
  async ({ payload }, { rejectWithValue }) => {
    try {
      const data = await submitProspectCompanyAPI(payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to submit prospect company'
      );
    }
  }
);

// Initial state
const initialState = {
  ncodes: { data: null, loading: false, error: null },
  result: { data: null, loading: false, error: null },
  quizSubmit: { data: null, loading: false, error: null },
  personalityOptions: { data: null, loading: false, error: null },
  personalitySubmit: { data: null, loading: false, error: null },
  innerNeedsSubmit: { data: null, loading: false, error: null },
  traitTestSubmit: { data: null, loading: false, error: null },
  tokenExpiryCheck: { data: null, loading: false, error: null },
  reorderBaselines: { data: null, loading: false, error: null },
  prospectSubmit: { data: null, loading: false, error: null },
};

const toolSlice = createSlice({
  name: 'tool',
  initialState,
  reducers: {
    clearNcodes: (state) => {
      state.ncodes.data = null;
      state.ncodes.error = null;
    },
    clearResult: (state) => {
      state.result.data = null;
      state.result.error = null;
    },
    clearQuizSubmit: (state) => {
      state.quizSubmit.data = null;
      state.quizSubmit.error = null;
    },
    clearPersonalityOptions: (state) => {
      state.personalityOptions.data = null;
      state.personalityOptions.error = null;
    },
    clearPersonalitySubmit: (state) => {
      state.personalitySubmit.data = null;
      state.personalitySubmit.error = null;
    },
    clearInnerNeedsSubmit: (state) => {
      state.innerNeedsSubmit.data = null;
      state.innerNeedsSubmit.error = null;
    },
    clearTraitTestSubmit: (state) => {
      state.traitTestSubmit.data = null;
      state.traitTestSubmit.error = null;
    },
    // ✅ NEW: Clear token expiry state
    clearTokenExpiryCheck: (state) => {
      state.tokenExpiryCheck.data = null;
      state.tokenExpiryCheck.error = null;
    },
    clearReorderBaselines: (state) => {
      state.reorderBaselines = { data: null, loading: false, error: null };
    },
    clearProspectSubmit: (state) => {
      state.prospectSubmit = { data: null, loading: false, error: null };
    },
    clearAllToolData: (state) => {
      state.ncodes = { data: null, loading: false, error: null };
      state.result = { data: null, loading: false, error: null };
      state.quizSubmit = { data: null, loading: false, error: null };
      state.personalityOptions = { data: null, loading: false, error: null };
      state.personalitySubmit = { data: null, loading: false, error: null };
      state.innerNeedsSubmit = { data: null, loading: false, error: null };
      state.traitTestSubmit = { data: null, loading: false, error: null };
      state.tokenExpiryCheck = { data: null, loading: false, error: null };
      state.reorderBaselines = { data: null, loading: false, error: null };
    },
  },
  extraReducers: (builder) => {
    // ncodes
    builder
      .addCase(fetchNcodes.pending, (state) => {
        state.ncodes.loading = true;
        state.ncodes.error = null;
      })
      .addCase(fetchNcodes.fulfilled, (state, action) => {
        state.ncodes.loading = false;
        state.ncodes.data = action.payload;
      })
      .addCase(fetchNcodes.rejected, (state, action) => {
        state.ncodes.loading = false;
        state.ncodes.error = action.payload;
      });

    // result
    builder
      .addCase(fetchResult.pending, (state) => {
        state.result.loading = true;
        state.result.error = null;
      })
      .addCase(fetchResult.fulfilled, (state, action) => {
        state.result.loading = false;
        state.result.data = action.payload;
      })
      .addCase(fetchResult.rejected, (state, action) => {
        state.result.loading = false;
        state.result.error = action.payload;
      });

    // quiz submit
    builder
      .addCase(submitQuiz.pending, (state) => {
        state.quizSubmit.loading = true;
        state.quizSubmit.error = null;
      })
      .addCase(submitQuiz.fulfilled, (state, action) => {
        state.quizSubmit.loading = false;
        state.quizSubmit.data = action.payload;
      })
      .addCase(submitQuiz.rejected, (state, action) => {
        state.quizSubmit.loading = false;
        state.quizSubmit.error = action.payload;
      });

    // personality test options
    builder
      .addCase(fetchPersonalityTestOptions.pending, (state) => {
        state.personalityOptions.loading = true;
        state.personalityOptions.error = null;
      })
      .addCase(fetchPersonalityTestOptions.fulfilled, (state, action) => {
        state.personalityOptions.loading = false;
        state.personalityOptions.data = action.payload;
      })
      .addCase(fetchPersonalityTestOptions.rejected, (state, action) => {
        state.personalityOptions.loading = false;
        state.personalityOptions.error = action.payload;
      });

    // personality test submit
    builder
      .addCase(submitPersonalityTest.pending, (state) => {
        state.personalitySubmit.loading = true;
        state.personalitySubmit.error = null;
      })
      .addCase(submitPersonalityTest.fulfilled, (state, action) => {
        state.personalitySubmit.loading = false;
        state.personalitySubmit.data = action.payload;
      })
      .addCase(submitPersonalityTest.rejected, (state, action) => {
        state.personalitySubmit.loading = false;
        state.personalitySubmit.error = action.payload;
      });

    // inner needs test submit
    builder
      .addCase(submitInnerNeedsTest.pending, (state) => {
        state.innerNeedsSubmit.loading = true;
        state.innerNeedsSubmit.error = null;
      })
      .addCase(submitInnerNeedsTest.fulfilled, (state, action) => {
        state.innerNeedsSubmit.loading = false;
        state.innerNeedsSubmit.data = action.payload;
      })
      .addCase(submitInnerNeedsTest.rejected, (state, action) => {
        state.innerNeedsSubmit.loading = false;
        state.innerNeedsSubmit.error = action.payload;
      });

    // trait test submit
    builder
      .addCase(submitTraitTest.pending, (state) => {
        state.traitTestSubmit.loading = true;
        state.traitTestSubmit.error = null;
      })
      .addCase(submitTraitTest.fulfilled, (state, action) => {
        state.traitTestSubmit.loading = false;
        state.traitTestSubmit.data = action.payload;
      })
      .addCase(submitTraitTest.rejected, (state, action) => {
        state.traitTestSubmit.loading = false;
        state.traitTestSubmit.error = action.payload;
      });

    // ✅ NEW: Token Expiry Check builders
    builder
      .addCase(checkTokenExpiry.pending, (state) => {
        state.tokenExpiryCheck.loading = true;
        state.tokenExpiryCheck.error = null;
      })
      .addCase(checkTokenExpiry.fulfilled, (state, action) => {
        state.tokenExpiryCheck.loading = false;
        state.tokenExpiryCheck.data = action.payload;
      })
      .addCase(checkTokenExpiry.rejected, (state, action) => {
        state.tokenExpiryCheck.loading = false;
        state.tokenExpiryCheck.error = action.payload;
      });

    // reorder baselines
    builder
      .addCase(reorderBaselines.pending, (state) => {
        state.reorderBaselines.loading = true;
        state.reorderBaselines.error = null;
      })
      .addCase(reorderBaselines.fulfilled, (state, action) => {
        state.reorderBaselines.loading = false;
        state.reorderBaselines.data = action.payload;
      })
      .addCase(reorderBaselines.rejected, (state, action) => {
        state.reorderBaselines.loading = false;
        state.reorderBaselines.error = action.payload;
      });

      // prospect submit
      builder
        .addCase(submitProspectCompany.pending, (state) => {
          state.prospectSubmit.loading = true;
          state.prospectSubmit.error = null;
        })
        .addCase(submitProspectCompany.fulfilled, (state, action) => {
          state.prospectSubmit.loading = false;
          state.prospectSubmit.data = action.payload;
        })
        .addCase(submitProspectCompany.rejected, (state, action) => {
          state.prospectSubmit.loading = false;
          state.prospectSubmit.error = action.payload;
        });
  },
});

export const {
  clearNcodes,
  clearResult,
  clearQuizSubmit,
  clearPersonalityOptions,
  clearPersonalitySubmit,
  clearInnerNeedsSubmit,
  clearAllToolData,
  clearTraitTestSubmit,
  clearTokenExpiryCheck,
  clearReorderBaselines,
  clearProspectSubmit,
} = toolSlice.actions;

export default toolSlice.reducer;

